/* eslint-disable max-lines */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { createFormConfigData } from '../../helper/form_builder';
import { errorHandler } from '../../helper/error_handler';
import FormApi from '../../apis/form';
import { isErrorWithMessage } from '../../helper';
import { RootState } from '../../redux/store';
import { FormObject, FormSettingsParams } from '../../params/redux/form';
import { resettingFormLoader, settingSelectedForm } from '../slices/form';

export interface AssignedForms {
    id: number;
    form_id: number;
    user_id: number;
    created_at: string;
    updated_at: string;
    form: FormObject;
}

interface FormResponseInterface extends Record<string, unknown> {
    success: 0 | 1;
    token?: string;
    data?: FormObject[];
    assigned_form?: AssignedForms[];
}

export const fetchFormsThunk = createAsyncThunk(
    'form/fetch',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response: FormResponseInterface = await FormApi.fetchForms(state.app.token, client_id || 0) as FormResponseInterface;
            if (response?.success === 1) {
                if (state.form.selectedForm) {
                    const selectedForm = response.data?.find((form: FormObject) => form.id === state.form.selectedForm?.id);
                    if (selectedForm) {
                        dispatch(settingSelectedForm(selectedForm));
                    }
                }
                return {
                    forms: response.data,
                    assignedForms: response.assigned_form
                };
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const createDefaultFormsThunk = createAsyncThunk(
    'form/default',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.createDefaultForms(state.app.token, client_id);
            if (response?.success === 1) {
                return response;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const connectExternalFormThunk = createAsyncThunk(
    'form/connect-external',
    async (data: {
        client_id: number;
        type: 'snapps-manual' | 'elementor-manual';
        form_name: string;
        pipeline_id: number;
        stage_id: number;
    }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.connectExternalForm(state.app.token, data);
            if (response?.success === 1) {
                return response.form_code;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const createUserFormThunk = createAsyncThunk(
    'form/create',
    async ({ client_id }: { client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data = {
                title: state.form.formFields.formTitle,
                form_json: createFormConfigData(state.form.formFields),
                type: 'internal',
                pipeline_id: state.form.formFields.pipelineId,
                stage_id: state.form.formFields.stageId,
                redirect_url: state.form.formFields.redirectUrl,
                redirect_msg: state.form.formFields.redirectMessage,
                template_id: state.form.formFields.formTemplateId,
                show_form: state.form.formFields.showTitle,
                client_id
            };
            const response = await FormApi.createUserForm(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const saveAsTemplateUserFormThunk = createAsyncThunk(
    'form/template',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data = {
                title: state.form.formFields.formTitle,
                form_json: createFormConfigData(state.form.formFields),
                type: 'internal',
                pipeline_id: state.form.formFields.pipelineId,
                stage_id: state.form.formFields.stageId,
                redirect_url: state.form.formFields.redirectUrl,
                redirect_msg: state.form.formFields.redirectMessage,
                template_id: state.form.formFields.formTemplateId,
                show_form: state.form.formFields.showTitle,
                client_id
            };
            const response = await FormApi.saveTemplateForm(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const changeStatusUserFormThunk = createAsyncThunk(
    'form/change-status',
    async (status: 'active' | 'draft', { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data = {
                form_id: state.form.selectedForm?.id,
                status
            };
            const response = await FormApi.changeFormStatus(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const fetchFacebookPagesThunk = createAsyncThunk(
    'form/facebook-pages',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.fetchFacebookPages(state.app.token, client_id);
            if (response?.success === 1) {
                return response?.facebook_pages;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const removeFacebookPagesThunk = createAsyncThunk(
    'form/remove-facebook-pages',
    async ({ client_id, page_id }: { client_id: number, page_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.disconnetFacebookPage(state.app.token, client_id, page_id);
            if (response?.success === 1) {
                await dispatch(fetchFacebookPagesThunk(client_id)).unwrap();
                return response;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const editUserFormThunk = createAsyncThunk(
    'form/edit',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const data = {
                form_id: state.form.selectedForm?.id,
                title: state.form.formFields.formTitle,
                form_json: createFormConfigData(state.form.formFields),
                pipeline_id: state.form.formFields.pipelineId,
                stage_id: state.form.formFields.stageId,
                redirect_url: state.form.formFields.redirectUrl,
                redirect_msg: state.form.formFields.redirectMessage,
                template_id: state.form.formFields.formTemplateId,
                show_form: state.form.formFields.showTitle,
                type: 'internal'
            };
            const response = await FormApi.editForm(state.app.token, data);
            if (response?.success === 1) {
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const deleteFormThunk = createAsyncThunk(
    'form/delete',
    async ({ form_id, client_id }: { form_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.deleteForm(state.app.token, form_id);
            if (response?.success === 1) {
                await dispatch(fetchFormsThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const duplicateFormThunk = createAsyncThunk(
    'form/duplicate',
    async ({ form_id, client_id }: { form_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.duplicateForm(state.app.token, form_id);
            if (response?.success === 1) {
                await dispatch(fetchFormsThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const assignFormThunk = createAsyncThunk(
    'form/assign',
    async ({ assignee_id, client_id }: { assignee_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.assignForm(state.app.token, state.form.selectedForm?.id || 0, assignee_id);
            if (response?.success === 1) {
                await dispatch(fetchFormsThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const removeAssigneeFormThunk = createAsyncThunk(
    'form/remove-assignee',
    async ({ assignee_id, client_id }: { assignee_id: number, client_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.removeAssignee(state.app.token, state.form.selectedForm?.id || 0, assignee_id);
            if (response?.success === 1) {
                await dispatch(fetchFormsThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const formSettingsThunk = createAsyncThunk(
    'form/settings',
    async (data: FormSettingsParams, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.formSettings(state.app.token, data);
            if (response?.success === 1) {
                await dispatch(fetchFormsThunk(data.client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const updateDefaultFormThunk = createAsyncThunk(
    'form/default-form-update',
    async (
        { forms_id, pipeline_id, stage_id, client_id }: { forms_id: number[], pipeline_id: number, stage_id: number, client_id: number }
        , { dispatch, rejectWithValue, getState }
    ): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.updateDefaultForm(state.app.token, forms_id, pipeline_id, stage_id);
            if (response?.success === 1) {
                await dispatch(fetchFormsThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const refetchAllPagesThunk = createAsyncThunk(
    'form/refetch-all-pages',
    async (client_id: number, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.refetchAllFacebookPages(state.app.token, client_id);
            if (response?.success === 1) {
                await dispatch(fetchFacebookPagesThunk(client_id)).unwrap();
                return true;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const previewRenderFormThunk = createAsyncThunk(
    'form/preview-render',
    async ({ form_json, form_title, template_id }: { form_json: string, form_title: string, template_id: number }, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.previewRenderForm(state.app.token, form_json, form_title, template_id);
            if (response?.success === 1) {
                return {
                    html: response?.html,
                    template_id
                };
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);

export const fetchFormTemplatesThunk = createAsyncThunk(
    'form/templates',
    async (_, { dispatch, rejectWithValue, getState }): Promise<unknown> => {
        try {
            const state = getState() as RootState;
            const response = await FormApi.fetchFormTemplates(state.app.token);
            if (response?.success === 1) {
                return response?.form_templates;
            }
            return errorHandler(response?.msg as string || 'Request failed', dispatch, rejectWithValue);
        } catch (error) {
            if (isErrorWithMessage(error)) {
                return errorHandler(error.message, dispatch, rejectWithValue);
            }
            return errorHandler('An unknown error occurred', dispatch, rejectWithValue);
        } finally {
            dispatch(resettingFormLoader());
        }
    }
);